import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Heart, ShoppingCart, ArrowLeft, Check, Minus, Plus } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useApp();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImage, setCurrentImage] = useState(null);

  const lang = i18n.language;
  const inWishlist = product ? isInWishlist(product.id) : false;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        setCurrentImage(response.data.image_url);
        if (response.data.colors?.length > 0) {
          setSelectedColor(response.data.colors[0]);
          // Check if there's a specific image for the first color
          if (response.data.color_images?.[response.data.colors[0]]) {
            setCurrentImage(response.data.color_images[response.data.colors[0]]);
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    // Update image if color-specific image exists
    if (product?.color_images?.[color]) {
      setCurrentImage(product.color_images[color]);
    } else {
      setCurrentImage(product?.image_url);
    }
  };

  const handleAddToCart = async () => {
    const success = await addToCart(product.id, quantity, selectedColor);
    if (success) {
      toast.success(t('shop.addToCart') + ' ✓', {
        description: `${quantity}x ${product.name[lang] || product.name.en}`,
        style: { background: '#1A1A1A', border: '1px solid #FF0099', color: '#fff' }
      });
    }
  };

  const handleWishlistToggle = async () => {
    if (inWishlist) {
      await removeFromWishlist(product.id);
      toast.success(t('shop.removeFromWishlist') + ' ✓');
    } else {
      await addToWishlist(product.id);
      toast.success(t('shop.addToWishlist') + ' ❤️');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#FF0099] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center">
        <p className="text-[#A3A3A3] text-lg mb-4">Product not found</p>
        <Link to="/shop">
          <Button className="btn-secondary">Back to Shop</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link 
          to="/shop" 
          className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#FF0099] transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Image */}
          <div className="relative aspect-square bg-[#1A1A1A] overflow-hidden">
            {product.badge && (
              <div className={`absolute top-4 left-4 z-10 ${product.badge === 'NEW' ? 'badge-new' : 'badge-sale'}`}>
                {product.badge}
              </div>
            )}
            <img
              src={currentImage || product.image_url}
              alt={product.name[lang] || product.name.en}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Details */}
          <div className="flex flex-col">
            {/* Category */}
            <span className="text-[#FF0099] text-sm uppercase tracking-wider font-bold mb-2">
              {product.category}
            </span>

            {/* Name */}
            <h1 className="font-heading text-3xl sm:text-4xl text-white font-bold mb-4">
              {product.name[lang] || product.name.en}
            </h1>

            {/* Price */}
            <p className="text-[#FF0099] font-heading font-bold text-4xl mb-6">
              €{product.price.toFixed(2)}
            </p>

            {/* Description */}
            <p className="text-[#A3A3A3] leading-relaxed mb-8">
              {product.description[lang] || product.description.en}
            </p>

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-6">
              {product.in_stock ? (
                <>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-500 text-sm font-bold uppercase">
                    {t('product.inStock')}
                  </span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <span className="text-red-500 text-sm font-bold uppercase">
                    {t('product.outOfStock')}
                  </span>
                </>
              )}
            </div>

            {/* Colors */}
            {product.colors && product.colors.length > 0 && (
              <div className="mb-8">
                <h3 className="text-white font-bold uppercase tracking-wider text-sm mb-4">
                  {t('product.colors')}: <span className="text-[#FF0099]">{selectedColor}</span>
                </h3>
                <div className="flex gap-3 flex-wrap">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center ${
                        selectedColor === color 
                          ? 'border-[#FF0099] scale-110' 
                          : 'border-transparent hover:border-white/50'
                      }`}
                      style={{ background: getColorValue(color) }}
                      title={color}
                      data-testid={`color-option-${color}`}
                    >
                      {selectedColor === color && (
                        <Check className="w-5 h-5 text-white drop-shadow-lg" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="text-white font-bold uppercase tracking-wider text-sm mb-4">
                {t('cart.quantity')}
              </h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 bg-[#1A1A1A] border border-[#333333] flex items-center justify-center hover:border-[#FF0099] transition-colors"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4 text-white" />
                </button>
                <span className="text-white font-bold text-xl w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 bg-[#1A1A1A] border border-[#333333] flex items-center justify-center hover:border-[#FF0099] transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleAddToCart}
                disabled={!product.in_stock}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                {t('shop.addToCart')}
              </Button>
              <Button
                onClick={handleWishlistToggle}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="wishlist-toggle-btn"
              >
                <Heart className={`w-5 h-5 ${inWishlist ? 'fill-[#FF0099] text-[#FF0099]' : ''}`} />
                {inWishlist ? t('shop.removeFromWishlist') : t('shop.addToWishlist')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getColorValue = (colorName) => {
  const colorMap = {
    'Rainbow': 'linear-gradient(135deg, #FF0099, #FAFF00, #00FFFF)',
    'Pink-Blue': 'linear-gradient(135deg, #FF69B4, #00BFFF)',
    'Yellow-Pink': 'linear-gradient(135deg, #FAFF00, #FF0099)',
    'Yellow': '#FAFF00',
    'Black': '#1A1A1A',
    'Red': '#FF0000',
    'Turquoise': '#00FFFF',
    'Leopard': '#8B4513',
    'Pink Gradient': 'linear-gradient(135deg, #FFB6C1, #FF69B4)',
    'Pink': '#FF69B4',
    'White': '#FFFFFF',
    'Purple': '#9B30FF',
    'Silver': '#C0C0C0',
    'Gold': '#FFD700',
    'Mixed Colors': 'linear-gradient(135deg, #FF0099, #FAFF00, #00FFFF, #9B30FF)'
  };
  return colorMap[colorName] || '#666666';
};
